/**
 * UserUploadDialog — bulk-create users from a CSV.
 *
 * Follows the master-file upload on CampusProfilePage: pick a file, run a
 * preflight check, and only then is Upload enabled. Two deliberate differences:
 *
 *  - **Every problem is listed at once**, not toasted one at a time. The master
 *    file precheck breaks on the first error, so a ten-row file takes ten passes
 *    to clean up. Here the whole list is on screen with spreadsheet row numbers.
 *  - **No base64 read.** The master file is handed to the backend as a file to
 *    parse; this parses in the browser and posts ordinary add-user payloads
 *    through the existing endpoint, so there is nothing to encode.
 *
 * All validation lives in `../userCsvUpload.js` — this component owns no rules.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  validateUserCsv, APPROVED_ROLE_NAMES, PASSWORD_MIN_LENGTH, REQUIRED_COLUMNS,
  approvedRoleLabel,
} from '../userCsvUpload.js';

const UserUploadDialog = ({
  isOpen,
  onClose,
  onUpload,
  roles,
  myRoleWeight,
  canAssignWeight,
  existingUsernames,
}) => {
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);      // { rows, headers }
  const [parseError, setParseError] = useState('');
  const [result, setResult] = useState(null);      // validateUserCsv output
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);  // { sent, total, requests }
  const inputRef = useRef(null);

  const reset = useCallback(() => {
    setFileName('');
    setParsed(null);
    setParseError('');
    setResult(null);
    setUploading(false);
    setProgress(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    if (uploading) return;
    reset();
    onClose();
  }, [uploading, reset, onClose]);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    reset();
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target?.result || '';
      const out = Papa.parse(text, {
        header: true,
        /* Blank rows are deliberately NOT skipped here. Excel writes comma-only
           lines for every row the sheet has ever touched, and they do need
           ignoring — but dropping them at parse time shifts every later index
           down, so an error on a row below an interior blank line would report a
           row number that is too low. validateUserCsv skips them itself while
           keeping the original index, which is what makes "Row 12" mean line 12
           of the file. */
        skipEmptyLines: false,
        transformHeader: (h) => String(h || '').trim().toLowerCase().replace(/^﻿/, ''),
      });
      if (out.errors?.length && !out.data?.length) {
        setParseError(out.errors[0]?.message || 'Could not read that file as CSV.');
        return;
      }
      setParsed({ rows: out.data || [], headers: out.meta?.fields || [] });
    };
    reader.onerror = () => setParseError('Could not read that file.');
    reader.readAsText(file);
  }, [reset]);

  const handleCheck = useCallback(() => {
    if (!parsed) return;
    setResult(validateUserCsv(parsed.rows, {
      roles,
      myRoleWeight,
      canAssignWeight,
      existingUsernames,
      headers: parsed.headers,
    }));
  }, [parsed, roles, myRoleWeight, canAssignWeight, existingUsernames]);

  const handleUpload = useCallback(async () => {
    if (!result?.ok || uploading) return;
    setUploading(true);
    setProgress({ sent: 0, total: result.payloads.length, requests: 0 });
    try {
      await onUpload(
        result.payloads,
        (sent, total, requests) => setProgress({ sent, total, requests }),
      );
      reset();
    } finally {
      /* The page closes the dialog on success and keeps it open on failure, so the
         only job here is releasing the button. */
      setUploading(false);
    }
  }, [result, uploading, onUpload, reset]);

  /* Issues grouped by row so a row with three problems reads as one entry. A file
     with a bad header has row === null and is shown first, on its own. */
  const grouped = useMemo(() => {
    if (!result?.issues?.length) return { fileLevel: [], rows: [] };
    const fileLevel = result.issues.filter((i) => i.row == null);
    const byRow = new Map();
    result.issues.filter((i) => i.row != null).forEach((i) => {
      if (!byRow.has(i.row)) byRow.set(i.row, []);
      byRow.get(i.row).push(i);
    });
    return {
      fileLevel,
      rows: [...byRow.entries()].sort((a, b) => a[0] - b[0]),
    };
  }, [result]);

  /* The role names THIS campus accepts from THIS uploader, taken from the live
     role list rather than from APPROVED_ROLE_NAMES.

     Showing the static list was the bug behind "Role admin is not available at
     this campus": the instructions promised a spelling the campus did not have
     under that exact name. Deriving it means the dialog can only ever advertise
     values that actually resolve. Falls back to the approved list when roles have
     not loaded yet, so the panel is never empty. */
  const usableRoleNames = useMemo(() => {
    const names = (roles || [])
      /* approvedRoleLabel, NOT a name-in-list check: the campus calls role 5
         "Administrator" while the approved spelling is "admin", so a raw name
         comparison drops it from this list — which is how the original bug hid
         itself. The validator resolves it either way; this is the half that has to
         agree with the validator, or the dialog advertises a different set of
         roles from the one it accepts. */
      .filter((r) => approvedRoleLabel(r?.user_role_name))
      .filter((r) => typeof canAssignWeight !== 'function'
        || canAssignWeight(r.user_role_weight, myRoleWeight))
      .map((r) => r.user_role_name)
      .filter(Boolean);
    return names.length ? names : APPROVED_ROLE_NAMES;
  }, [roles, canAssignWeight, myRoleWeight]);

  if (!isOpen) return null;

  const checked = result != null;

  return (
    <div className="pp-dialog-backdrop" role="presentation" onClick={handleClose}>
      <div
        className="pp-dialog pp-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="up-upload-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pp-dialog__header pp-dialog__header--top">
          <h2 className="pp-dialog__title" id="up-upload-title">Upload Users</h2>
          <button
            type="button"
            className="up-drawer__close"
            onClick={handleClose}
            disabled={uploading}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="pp-dialog__body pp-dialog__body--stack">
          <p className="up-upload__intro">
            A CSV with a header row and one user per row. Columns:
            {' '}<code>first</code>, <code>last</code>, <code>username</code>,{' '}
            <code>email</code>, <code>role</code>, <code>password</code>.
          </p>
          <ul className="up-upload__rules">
            <li><strong>Required:</strong> {REQUIRED_COLUMNS.join(', ')}.</li>
            <li>
              <strong>email</strong> is required for every role except{' '}
              <code>caregiver</code>.
            </li>
            <li>
              <strong>role</strong> must be one of: {usableRoleNames.join(', ')}.
              {' '}Case, spaces and hyphens are ignored.
            </li>
            <li>
              <strong>password</strong> — {PASSWORD_MIN_LENGTH} characters minimum,
              letters and numbers only.
            </li>
            <li>
              <strong>last</strong> becomes the user&apos;s display name.{' '}
              <code>first</code> is ignored.
            </li>
          </ul>

          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={uploading}
            className="up-upload__input"
            aria-label="Choose a CSV file"
          />

          {fileName ? (
            <p className="up-upload__file">Selected: <strong>{fileName}</strong></p>
          ) : null}

          {parseError ? (
            <p className="up-upload__status up-upload__status--bad">{parseError}</p>
          ) : null}

          {checked && result.ok ? (
            <p className="up-upload__status up-upload__status--good">
              File good — {result.payloads.length}
              {result.payloads.length === 1 ? ' user' : ' users'} ready to upload.
            </p>
          ) : null}

          {checked && !result.ok ? (
            <div className="up-upload__issues">
              <p className="up-upload__status up-upload__status--bad">
                {result.issues.length}
                {result.issues.length === 1 ? ' problem' : ' problems'} found. Nothing
                has been uploaded — fix the file and check it again.
              </p>
              {grouped.fileLevel.length ? (
                <ul className="up-upload__issue-list">
                  {grouped.fileLevel.map((i, n) => (
                    <li key={`f${n}`}>{i.message}</li>
                  ))}
                </ul>
              ) : null}
              {grouped.rows.length ? (
                <ul className="up-upload__issue-list">
                  {grouped.rows.map(([rowNum, items]) => (
                    <li key={rowNum}>
                      <strong>Row {rowNum}:</strong>{' '}
                      {items.map((i) => i.message).join(' ')}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}

          {uploading && progress ? (
            <p className="up-upload__status">
              {/* An X-of-Y counter is only honest when there is more than one
                  request. With a single request holding every row there is no
                  intermediate progress to report — the count would sit on "0 of 30"
                  for the entire wait and then vanish, which reads as a stalled
                  upload rather than a working one. */}
              {progress.requests > 1
                ? `Uploading… ${progress.sent} of ${progress.total}`
                : `Uploading ${progress.total} ${progress.total === 1 ? 'user' : 'users'}…`}
            </p>
          ) : null}
        </div>

        <div className="pp-dialog__footer">
          <button
            type="button"
            className="up-btn up-btn--secondary"
            onClick={handleClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="up-btn up-btn--secondary"
            onClick={handleCheck}
            disabled={!parsed || uploading}
          >
            Check File
          </button>
          <button
            type="button"
            className="up-btn up-btn--primary"
            onClick={handleUpload}
            disabled={!result?.ok || uploading}
            title={result?.ok ? undefined : 'Check the file first.'}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserUploadDialog;
